import {
  extractIccidCandidates,
  extractImeiCandidates,
  formatMoney,
  iccidRejectionReason,
  imeiRejectionReason,
  queryKeys,
  type SkuResolution,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  useToast,
} from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import JsBarcode from 'jsbarcode'
import { Camera, Keyboard, Printer } from 'lucide-react'
import QRCode from 'qrcode'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { api } from '../lib/api.js'
import { LABEL_PRESETS, type LabelPreset, printLabels } from '../lib/label-print.js'
import { recognize, terminateOcr } from '../lib/ocr.js'

type IdentifierType = 'imei' | 'iccid' | 'sku'
type InputMode = 'type' | 'photo'

interface Candidate {
  value: string
  reason: string | null // null = valid
}

const TYPE_LABEL: Record<IdentifierType, string> = {
  imei: 'IMEI',
  iccid: 'SIM / ICCID',
  sku: 'SKU',
}

/** Digits-only checks apply to imei/iccid; a SKU is freeform text, so OCR for
 *  it just offers raw lines rather than trying to validate a shape. */
function rejectionFor(type: IdentifierType, value: string): string | null {
  if (!value.trim()) return 'Enter a value'
  if (type === 'imei') return imeiRejectionReason(value)
  if (type === 'iccid') return iccidRejectionReason(value)
  // SKU: Code 128 encodes ASCII, so anything outside that can't be barcoded.
  // biome-ignore lint/suspicious/noControlCharactersInRegex: Code 128 range check
  if (!/^[\x00-\x7F]+$/.test(value)) return 'Barcode can only encode plain ASCII text'
  return null
}

/** Waits for typing to settle before resolving a SKU against the catalog,
 *  so each keystroke doesn't fire its own request. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return settled
}

function candidatesFor(type: IdentifierType, rawText: string): Candidate[] {
  if (type === 'sku') {
    return [
      ...new Set(
        rawText
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter(Boolean),
      ),
    ].map((v) => ({
      value: v,
      reason: null,
    }))
  }
  const { candidates } =
    type === 'imei' ? extractImeiCandidates(rawText) : extractIccidCandidates(rawText)
  const seen = new Set<string>()
  const out: Candidate[] = []
  for (const c of candidates) {
    if (seen.has(c)) continue
    seen.add(c)
    out.push({
      value: c,
      reason: type === 'imei' ? imeiRejectionReason(c) : iccidRejectionReason(c),
    })
  }
  return out
}

/**
 * Photograph an IMEI, SIM/ICCID or SKU and turn it into a printable barcode
 * label. OCR is assistive only: recognized candidates are shown for the
 * staff member to confirm or edit, never applied automatically, and nothing
 * can be printed until the value validates.
 */
export function LabelsPage() {
  const toast = useToast()
  // Not schema-validated: this route only ever receives these from the
  // "Print label" action on a product row, which sets them all itself.
  const search = useSearch({ strict: false }) as {
    sku?: string
    /** From a provisioning unit's "Print price label": its IMEI/ICCID. */
    identifier?: string | number
    identifierType?: 'imei' | 'iccid'
    name?: string
    priceCents?: number
  }
  const [type, setType] = useState<IdentifierType>('imei')
  const [mode, setMode] = useState<InputMode>('type')
  const [value, setValue] = useState('')
  const [productName, setProductName] = useState('')
  const [priceText, setPriceText] = useState('')

  const [ocrStatus, setOcrStatus] = useState<'idle' | 'loading' | 'running' | 'done' | 'error'>(
    'idle',
  )
  const [ocrProgress, setOcrProgress] = useState(0)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [rawText, setRawText] = useState('')
  const [showRaw, setShowRaw] = useState(false)

  const [qrEnabled, setQrEnabled] = useState(false)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [preset, setPreset] = useState<LabelPreset>('2.25x1.25')
  const [copies, setCopies] = useState(1)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const barcodeRef = useRef<SVGSVGElement>(null)

  const rejection = rejectionFor(type, value)
  const candidates = useMemo(() => candidatesFor(type, rawText), [type, rawText])

  // A "Print label" link from a product row or a provisioning unit lands here
  // already knowing the SKU or IMEI/ICCID, name and price — fill the form from it once, on mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: prefill from the link once, on mount — not on every search-object identity change
  useEffect(() => {
    if (!search.sku && search.identifier == null) return
    if (search.identifier != null) {
      setType(search.identifierType ?? 'imei')
      // String(): the router's JSON search parsing turns an all-digit IMEI into a number.
      setValue(String(search.identifier))
    } else if (search.sku) {
      setType('sku')
      setValue(search.sku)
    }
    if (search.name) setProductName(search.name)
    if (search.priceCents != null) setPriceText(formatMoney(search.priceCents))
  }, [])

  // Release the OCR worker when the page unmounts — otherwise the wasm
  // instance it holds is never freed.
  useEffect(() => () => void terminateOcr(), [])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  useEffect(() => {
    if (rejection || !barcodeRef.current) return
    try {
      // displayValue is off: the printed template already draws the value as
      // its own caption line (see label-print.ts), and turning both on
      // printed it twice. Leaving it off keeps the barcode's own SVG text
      // from duplicating that caption in the on-screen preview too.
      JsBarcode(barcodeRef.current, value, { format: 'CODE128', displayValue: false, margin: 8 })
    } catch {
      // A value can pass rejectionFor's ASCII check yet still contain a
      // character jsbarcode's CODE128 encoder can't represent (e.g. DEL);
      // leave the previous render rather than crash the page.
    }
  }, [value, rejection])

  useEffect(() => {
    if (!qrEnabled || rejection) {
      setQrDataUrl(null)
      return
    }
    let cancelled = false
    QRCode.toDataURL(value, { margin: 1 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url)
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null)
      })
    return () => {
      cancelled = true
    }
  }, [qrEnabled, value, rejection])

  // A typed or scanned SKU can resolve to a real product without a link —
  // fill name/price from the catalog once the value settles, but only into
  // fields staff haven't already touched (never overwrite what they typed).
  const settledSku = useDebounced(type === 'sku' && !rejection ? value : '', 350)
  const { data: skuResolution } = useQuery({
    queryKey: queryKeys.products.skuResolution([settledSku]),
    enabled: settledSku.length > 0,
    queryFn: () =>
      api.post<{ results: SkuResolution[] }>('/products/resolve-skus', { skus: [settledSku] }),
    staleTime: 30_000,
  })
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-fills on a new resolution, not on every keystroke into the fields it fills
  useEffect(() => {
    const match = skuResolution?.results[0]?.match
    if (!match) return
    if (!productName) setProductName(match.name)
    if (!priceText) setPriceText(formatMoney(match.salePriceCents ?? match.priceCents))
  }, [skuResolution])

  // A typed or scanned IMEI/ICCID that belongs to a known device fills in its
  // name and the price it actually sells for — its attribute price when it has
  // one, else the model's — again only into fields staff haven't touched.
  const settledUnit = useDebounced(type !== 'sku' && !rejection ? value : '', 350)
  const { data: unitResolution } = useQuery({
    queryKey: queryKeys.units.list({ q: settledUnit, labelLookup: true }),
    enabled: settledUnit.length >= 5,
    staleTime: 30_000,
    queryFn: async () => {
      const found = await api.get<{
        items: {
          imei: string
          productId: string
          productName: string
          attributePriceCents?: number | null
        }[]
      }>('/units', { query: { q: settledUnit, pageSize: 5 } })
      const unit = found.items.find((u) => u.imei === settledUnit)
      if (!unit) return null
      let priceCents = unit.attributePriceCents ?? null
      if (priceCents == null) {
        try {
          const product = await api.get<{ priceCents: number; salePriceCents: number | null }>(
            `/products/${unit.productId}`,
          )
          priceCents = product.salePriceCents ?? product.priceCents
        } catch {
          // no price rather than a wrong one
        }
      }
      return { name: unit.productName, priceCents }
    },
  })
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-fills on a new resolution, not on every keystroke into the fields it fills
  useEffect(() => {
    if (!unitResolution) return
    if (!productName) setProductName(unitResolution.name)
    if (!priceText && unitResolution.priceCents != null) {
      setPriceText(formatMoney(unitResolution.priceCents))
    }
  }, [unitResolution])

  const runOcr = async (file: File) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(URL.createObjectURL(file))
    setRawText('')
    setOcrProgress(0)
    setOcrStatus('loading')
    try {
      const text = await recognize(file, {
        digitsOnly: type !== 'sku',
        onProgress: (fraction) => {
          setOcrStatus('running')
          setOcrProgress(fraction)
        },
      })
      setRawText(text)
      setOcrStatus('done')
    } catch (error) {
      setOcrStatus('error')
      toast.error('Could not read the photo', error instanceof Error ? error.message : undefined)
    }
  }

  const handlePrint = () => {
    if (rejection || !barcodeRef.current) return
    printLabels({
      barcodeSvg: barcodeRef.current.outerHTML,
      qrDataUrl: qrEnabled && qrDataUrl ? qrDataUrl : undefined,
      text: value,
      name: productName.trim() || undefined,
      price: priceText.trim() || undefined,
      preset,
      copies,
    })
  }

  return (
    <Page>
      <PageHeader
        title="Labels"
        description="Turn an IMEI, SIM/ICCID or SKU into a barcode you can print."
      />

      <div className="grid gap-5 lg:grid-cols-[420px_1fr]">
        <Card className="h-fit">
          <CardContent className="space-y-4 pt-4">
            <Field label="Identifier type">
              {(props) => (
                <Select value={type} onValueChange={(v) => setType(v as IdentifierType)}>
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="imei">IMEI</SelectItem>
                    <SelectItem value="iccid">SIM / ICCID</SelectItem>
                    <SelectItem value="sku">SKU</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>

            <div className="flex gap-1 rounded-md bg-surface-sunken p-1">
              <Button
                type="button"
                variant={mode === 'type' ? 'secondary' : 'ghost'}
                size="sm"
                className="flex-1"
                onClick={() => setMode('type')}
              >
                <Keyboard /> Type
              </Button>
              <Button
                type="button"
                variant={mode === 'photo' ? 'secondary' : 'ghost'}
                size="sm"
                className="flex-1"
                onClick={() => setMode('photo')}
              >
                <Camera /> Take photo
              </Button>
            </div>

            {mode === 'type' ? (
              <Field label={TYPE_LABEL[type]}>
                {(props) => (
                  <Input
                    {...props}
                    identifier
                    autoFocus
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={type === 'sku' ? 'ACC-CASE-01' : '000000000000000'}
                  />
                )}
              </Field>
            ) : (
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    e.target.value = ''
                    if (file) void runOcr(file)
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera /> {previewUrl ? 'Retake photo' : `Photograph the ${TYPE_LABEL[type]}`}
                </Button>

                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Captured label"
                    className="max-h-40 w-full rounded-md border border-border object-contain"
                  />
                ) : null}

                {ocrStatus === 'loading' ? (
                  <Alert tone="info">Loading the OCR engine (first use only)…</Alert>
                ) : null}
                {ocrStatus === 'running' ? (
                  <Alert tone="info">Reading photo… {Math.round(ocrProgress * 100)}%</Alert>
                ) : null}

                {candidates.length > 0 ? (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-foreground-muted">
                      {type === 'sku' ? 'Lines found — pick one' : 'Numbers found — pick one'}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {candidates.map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => setValue(c.value)}
                          className="font-identifier"
                        >
                          <Badge tone={c.reason ? 'warning' : 'success'}>
                            {c.value}
                            {c.reason ? ` — ${c.reason}` : ''}
                          </Badge>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {ocrStatus === 'done' && candidates.length === 0 ? (
                  <Alert tone="warning">
                    Nothing that looks like a {TYPE_LABEL[type]} was found.
                  </Alert>
                ) : null}

                {rawText ? (
                  <details open={showRaw} onToggle={(e) => setShowRaw(e.currentTarget.open)}>
                    <summary className="cursor-pointer text-xs text-foreground-muted">
                      Show raw text read from the photo
                    </summary>
                    <pre className="mt-1 max-h-32 overflow-auto rounded-md border border-border bg-surface-sunken p-2 text-xs whitespace-pre-wrap">
                      {rawText}
                    </pre>
                  </details>
                ) : null}
              </div>
            )}

            <Field label="Value to encode" hint="Confirm or edit before printing.">
              {(props) => (
                <Input
                  {...props}
                  identifier
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  invalid={Boolean(value && rejection)}
                />
              )}
            </Field>
            {value && rejection ? <Alert tone="danger">{rejection}</Alert> : null}

            <Field label="Product name" hint="Optional — printed on the label above the barcode.">
              {(props) => (
                <Input
                  {...props}
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Nokia 3310 4G"
                />
              )}
            </Field>

            <Field label="Price" hint="Optional — printed on the label.">
              {(props) => (
                <Input
                  {...props}
                  value={priceText}
                  onChange={(e) => setPriceText(e.target.value)}
                  placeholder="$29.99"
                />
              )}
            </Field>

            <div className="flex items-center gap-3">
              <Switch id="labels-qr" checked={qrEnabled} onCheckedChange={setQrEnabled} />
              <label className="text-sm" htmlFor="labels-qr">
                Also include a QR code
              </label>
            </div>

            <Field label="Label size">
              {(props) => (
                <Select value={preset} onValueChange={(v) => setPreset(v as LabelPreset)}>
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(LABEL_PRESETS) as LabelPreset[]).map((p) => (
                      <SelectItem key={p} value={p}>
                        {LABEL_PRESETS[p].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Copies">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={1}
                  max={30}
                  value={copies}
                  onChange={(e) =>
                    setCopies(Math.min(30, Math.max(1, Number.parseInt(e.target.value, 10) || 1)))
                  }
                />
              )}
            </Field>
          </CardContent>
        </Card>

        <Card className="h-fit">
          <CardContent className="flex flex-col items-center gap-4 pt-8 pb-8">
            {/* Always mounted so the barcode effect always has a target — hidden
                rather than unmounted while there is nothing valid to encode. */}
            <div
              className={
                rejection
                  ? 'hidden'
                  : 'flex flex-col items-center gap-3 rounded-md border border-border p-6'
              }
            >
              {productName ? (
                <p className="max-w-52 truncate text-sm font-semibold">{productName}</p>
              ) : null}
              <svg ref={barcodeRef} />
              {qrEnabled && qrDataUrl ? (
                <img src={qrDataUrl} alt="QR code" className="size-24" />
              ) : null}
              {/* The barcode SVG no longer draws its own text (see the
                  JsBarcode call above), so mirror the printed label's value
                  caption here to keep the preview matching what prints. */}
              <p className="font-identifier text-sm tracking-wide">{value}</p>
              {priceText ? <p className="text-lg font-bold">{priceText}</p> : null}
            </div>
            {rejection ? (
              <p className="text-sm text-foreground-muted">
                Enter a valid {TYPE_LABEL[type]} to preview the barcode.
              </p>
            ) : (
              <Button variant="primary" size="lg" onClick={handlePrint}>
                <Printer /> Print {copies > 1 ? `${copies} labels` : 'label'}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </Page>
  )
}
